import currency from 'currency.js';
import { ErrorUtility } from '../errors';
import { ActionStatusEnum, PaymentMethodEnum } from '../status';
/**
 * NOTE: Any changes to this file should be reflected in the Kotlin implementation
 * in pointcraft-types/generated/kotlin/src/main/kotlin/com/am3ndorg/pointcraft/calculations/
 */
/**
 * Rounds a number to 2 decimal places using currency.js for financial safety.
 *
 * @param {number} value - The numeric value to be rounded.
 * @returns {number} - The rounded value.
 *
 * @example
 * const rounded = round2(123.456); // returns 123.46
 * const rounded2 = round2(123.454); // returns 123.45
 */
export const round2 = (value) => {
    return currency(value).value;
};
/**
 * Converts a numeric value to a currency-formatted number with appropriate rounding.
 *
 * @param {number} value - The numeric value to be converted to a currency value.
 * @returns {number} - The currency-formatted value as a number.
 *
 * @example
 * const formattedValue = toCurrencyValue(123.456); // returns 123.46
 */
export const toCurrencyValue = (value) => {
    const val = currency(value, { symbol: '$' });
    return val.value;
};
/**
 * Calculates the total processing fees based on the provided amount and pricing configuration.
 * Splits fees between customer and merchant based on customerSplits configuration.
 *
 * @param {number} amount - The total amount before processing fees are applied.
 * @param {ProcessingConfigType} pricingConfig - The configuration for processing fees, including rate, flat fee rules, and customer/merchant splits.
 * @returns {object} - An object containing the total fee, breakdown of fees by type and payer, store net amount, and customer fees total.
 *
 * @property {number} totalProcessingFee - Total processing fees (percentage + flat)
 * @property {object} breakdown - Detailed breakdown of fees
 * @property {number} breakdown.MerchantPercentageFee - Percentage fee portion paid by merchant
 * @property {number} breakdown.MerchantFlatFee - Flat fee portion paid by merchant
 * @property {number} breakdown.CustomerPercentageFee - Percentage fee portion paid by customer
 * @property {number} breakdown.CustomerFlatFee - Flat fee portion paid by customer
 * @property {number} storeNetAmount - Net amount merchant receives (original amount - merchant's portion of fees)
 * @property {number} customerFeesTotal - Total fees paid by customer (CustomerPercentageFee + CustomerFlatFee)
 *
 * @example
 * // Customer pays all percentage fees, 15% of flat fees
 * const fees = calculateProcessingFees(100, {
 *   rate: 0.0265,
 *   flatFeeRules: [{ min: 0, max: 1000000, flatFee: 0.29 }],
 *   customerSplits: { rate: 1.0, fixed: 0.15 }
 * });
 * // returns {
 * //   totalProcessingFee: 2.94,
 * //   breakdown: {
 * //     MerchantPercentageFee: 0,
 * //     MerchantFlatFee: 0.25,
 * //     CustomerPercentageFee: 2.65,
 * //     CustomerFlatFee: 0.04
 * //   },
 * //   storeNetAmount: 99.75,  // 100 - 0.25 (merchant's portion of flat fee)
 * //   customerFeesTotal: 2.69  // 2.65 + 0.04 (customer pays this on top)
 * // }
 */
export function calculateProcessingFees(amount, pricingConfig) {
    if (amount <= 0) {
        return {
            totalProcessingFee: 0,
            breakdown: {
                MerchantPercentageFee: 0,
                MerchantFlatFee: 0,
                CustomerPercentageFee: 0,
                CustomerFlatFee: 0,
            },
            storeNetAmount: 0,
            customerFeesTotal: 0,
        };
    }
    // Calculate base fees
    const percentageFee = currency(amount).multiply(pricingConfig.rate).value;
    const applicableRule = pricingConfig.flatFeeRules.find((rule) => amount >= rule.min && amount <= rule.max);
    const flatFee = applicableRule?.flatFee || 0;
    // Get customer splits or default to 0 (merchant pays all)
    const customerRateSplit = pricingConfig.customerSplits?.rate ?? 0;
    const customerFixedSplit = pricingConfig.customerSplits?.fixed ?? 0;
    // Calculate customer and merchant portions
    const CustomerPercentageFee = currency(percentageFee).multiply(customerRateSplit).value;
    const MerchantPercentageFee = currency(percentageFee).subtract(CustomerPercentageFee).value;
    const CustomerFlatFee = currency(flatFee).multiply(customerFixedSplit).value;
    const MerchantFlatFee = currency(flatFee).subtract(CustomerFlatFee).value;
    // Total fees
    const totalProcessingFee = currency(percentageFee).add(flatFee).value;
    const customerFeesTotal = currency(CustomerPercentageFee).add(CustomerFlatFee).value;
    const merchantFeesTotal = currency(MerchantPercentageFee).add(MerchantFlatFee).value;
    // Calculate store net amount
    // Store net = amount - merchant's portion of fees
    // When customer pays all fees (splits = 1), merchant gets full amount
    // When merchant pays all fees (splits = 0), merchant gets amount minus total fees
    const storeNetAmount = currency(amount).subtract(merchantFeesTotal).value;
    return {
        totalProcessingFee,
        breakdown: {
            MerchantPercentageFee,
            MerchantFlatFee,
            CustomerPercentageFee,
            CustomerFlatFee,
        },
        storeNetAmount,
        customerFeesTotal,
    };
}
/**
 * Verifies the integrity of checkout totals by recalculating and comparing them with provided data.
 *
 * @param {object} params - The parameters object.
 * @param {CheckoutDataType} params.checkoutData - The checkout data including items and financial information.
 * @param {ProcessingConfigType} params.processingConfig - The processing fee configuration.
 * @returns {VerifyCheckoutTotalsReturn} - An object indicating validation status, errors, and detailed totals.
 *
 * @example
 * const result = verifyCheckoutTotals({ checkoutData, processingConfig });
 * if (result.isValidated) {
 *   // Proceed with checkout
 * } else {
 *   // Handle validation errors
 * }
 */
export function verifyCheckoutTotals({ checkoutData, processingConfig, }) {
    // Assignment of Variables
    const CHECKOUT_TOTAL = currency(checkoutData.financialInfo.total).value;
    const PROCESSING_FEES = currency(checkoutData.financialInfo.processingFees).value;
    const HAS_CUSTOMER_FEES = processingConfig.customerSplits &&
        (processingConfig.customerSplits.rate ||
            processingConfig.customerSplits.fixed);
    const ITEMS = checkoutData.items;
    const ADJUSTMENT = currency(checkoutData.financialInfo.adjustment).value;
    const TAXES = currency(checkoutData.financialInfo.taxes).value;
    const DISCOUNT = currency(checkoutData.financialInfo.discount).value;
    /// End of Assignment of Variables
    const resultObject = {
        isValidated: true,
        errors: [],
        totals: {
            itemsSubTotal: 0,
            totalsBeforeProcessing: 0,
            calculatedTotal: 0,
            calculatedProcessingFees: 0,
            processingConfig,
        },
    };
    const itemsSubTotal = ITEMS.reduce((acc, item) => {
        return currency(acc).add(currency(item.price).multiply(item.quantity))
            .value;
    }, 0);
    const totalsBeforeProcessing = currency(itemsSubTotal)
        .add(ADJUSTMENT)
        .add(TAXES)
        .subtract(DISCOUNT).value;
    const calculatedTotal = currency(totalsBeforeProcessing).add(PROCESSING_FEES).value;
    /**
     *  Verifications for the checkout data
     */
    if (typeof CHECKOUT_TOTAL !== 'number' ||
        isNaN(CHECKOUT_TOTAL) ||
        CHECKOUT_TOTAL <= 0 ||
        CHECKOUT_TOTAL > 1000000) {
        resultObject.errors.push(new ErrorUtility.PAYMENT_INVALID_NUMBER('Error during checkout, #P14', 
        // invalid number value this is what was given/passed
        `Invalid number value got: ${CHECKOUT_TOTAL}`));
    }
    if (calculatedTotal !== CHECKOUT_TOTAL) {
        resultObject.errors.push(new ErrorUtility.PAYMENT_CHECKOUT_TOTAL_NOT_EQUAL('Error during checkout, #P15', `Checkout total does not match, expected: ${calculatedTotal}, got: ${CHECKOUT_TOTAL}`));
    }
    const calculatedProcessingFees = calculateProcessingFees(totalsBeforeProcessing, processingConfig);
    if (checkoutData?.financialInfo.merchantProcessingFees != null) {
        if (calculatedProcessingFees.totalProcessingFee !==
            checkoutData.financialInfo.merchantProcessingFees) {
            resultObject.errors.push(new ErrorUtility.PAYMENT_PROCESSING_FEES_NOT_EQUAL('Error during checkout, #P16', `Merchant Processing fees do not match, expected: ${calculatedProcessingFees.totalProcessingFee}, got: ${checkoutData.financialInfo.merchantProcessingFees}`));
        }
    }
    if (HAS_CUSTOMER_FEES &&
        PROCESSING_FEES !== calculatedProcessingFees.customerFeesTotal) {
        resultObject.errors.push(new ErrorUtility.PAYMENT_PROCESSING_FEES_NOT_EQUAL('Error during checkout, #P16', `Customer Processing fees do not match, expected: ${calculatedProcessingFees.customerFeesTotal}, got: ${checkoutData.financialInfo.processingFees}`));
    }
    // Verify checkout payments if exists and make sure payments are not greater than total. And merchantNetAmount is correct and not greater than total or negative
    if (checkoutData.financialInfo.payment?.transactions?.length > 0) {
        const paymentTotals = getPaymentTotals(checkoutData.financialInfo.payment.transactions);
        // Verify total payments don't exceed checkout total
        if (paymentTotals.totalAmount > CHECKOUT_TOTAL) {
            resultObject.errors.push(new ErrorUtility.PAYMENT_AMOUNT_EXCEEDS_TOTAL('Error during checkout, #P17', `Total payments (${paymentTotals.totalAmount}) exceed checkout total (${CHECKOUT_TOTAL})`));
        }
        // Verify each payment's merchantNetAmount
        checkoutData.financialInfo.payment.transactions.forEach((payment) => {
            if (payment.status === ActionStatusEnum.SUCCESS) {
                // Check if merchantNetAmount is negative
                if (payment.merchantNetAmount < 0) {
                    resultObject.errors.push(new ErrorUtility.PAYMENT_INVALID_MERCHANT_NET_AMOUNT('Error during checkout, #P18', `Merchant net amount cannot be negative for payment ${payment.id}: ${payment.merchantNetAmount}`));
                }
                // Check if merchantNetAmount is greater than the payment amount
                if (payment.merchantNetAmount > payment.amount) {
                    resultObject.errors.push(new ErrorUtility.PAYMENT_INVALID_MERCHANT_NET_AMOUNT('Error during checkout, #P19', `Merchant net amount (${payment.merchantNetAmount}) cannot exceed payment amount (${payment.amount}) for payment ${payment.id}`));
                }
            }
        });
        // Calculate expected merchant net amount and compare with the total from payments
        const expectedMerchantNetTotal = calculatedProcessingFees.storeNetAmount;
        // Only validate if we have merchant net amounts in the payments
        if (paymentTotals.totalMerchantNetAmount > 0) {
            // Allow for small rounding differences (within 1 cent)
            const difference = Math.abs(paymentTotals.totalMerchantNetAmount - expectedMerchantNetTotal);
            if (difference > 0.01) {
                resultObject.errors.push(new ErrorUtility.PAYMENT_MERCHANT_NET_TOTAL_MISMATCH('Error during checkout, #P20', `Total merchant net amount from payments (${paymentTotals.totalMerchantNetAmount}) does not match expected amount (${expectedMerchantNetTotal})`));
            }
        }
    }
    // End of Verifications for the checkout data
    resultObject.isValidated = resultObject.errors.length === 0;
    resultObject.totals = {
        itemsSubTotal,
        totalsBeforeProcessing,
        calculatedTotal,
        calculatedProcessingFees: calculatedProcessingFees.customerFeesTotal,
        processingConfig,
    };
    return resultObject;
}
/**
 * Calculates the taxes from the items based on the tax rate.
 *
 * @param {object} params - The parameters object.
 * @param {CheckoutItemType[]} params.items - The items to calculate the taxes from.
 * @param {number} params.taxRate - The tax rate to use.
 * @returns {number} - The total taxes.
 *
 * @example
 * const taxes = calculateTaxesFromItems({ items, taxRate });
 */
export function calculateTaxesFromItems({ items, taxRate, taxableBase, }) {
    if (taxRate === 0) {
        return 0;
    }
    // If taxableBase is provided, use it directly
    if (taxableBase !== undefined) {
        return currency(taxableBase).multiply(taxRate).value;
    }
    // Otherwise, calculate based on taxable items
    let totalTaxes = 0;
    items.forEach((item) => {
        if (item?.metadata?.isTaxable) {
            totalTaxes = currency(totalTaxes).add(currency(item.price).multiply(item.quantity).multiply(taxRate)).value;
        }
    });
    return totalTaxes;
}
/**
 * Calculates the totals for a sale.
 *
 * @param {object} params - The parameters object.
 * @param {CheckoutItemType[]} params.items - The items to calculate the totals from.
 * @param {number} params.taxRate - The tax rate to use.
 * @param {boolean} params.clientPaysFees - Whether the client pays the fees.
 * @param {number} params.adjustment - The adjustment to the total.
 * @param {ProcessingConfigType} params.pricingConfig - The pricing configuration.
 * @returns {object} - The totals for the sale.
 *
 * @example
 * const totals = calculateTotalsForSale({ items, taxRate, clientPaysFees, adjustment, pricingConfig });
 */
export function calculateTotalsForSale({ items, taxRate, clientPaysFees, adjustment, pricingConfig, }) {
    let total = 0;
    let itemsSubTotal = 0;
    let subTotal = 0;
    let cost = 0;
    let taxes = 0;
    let processingFees = 0;
    items.forEach((item) => {
        itemsSubTotal = currency(itemsSubTotal).add(currency(item.price).multiply(item.quantity)).value;
        cost = currency(cost).add(currency(item.cost).multiply(item.quantity)).value;
    });
    subTotal = currency(itemsSubTotal).add(adjustment ?? 0).value;
    // Calculate taxable items subtotal
    const taxableItemsSubTotal = items.reduce((acc, item) => {
        if (item?.metadata?.isTaxable) {
            return currency(acc).add(currency(item.price).multiply(item.quantity))
                .value;
        }
        return acc;
    }, 0);
    // Per business rule: positive adjustments are always taxed
    const positiveAdjustment = (adjustment ?? 0) > 0 ? (adjustment ?? 0) : 0;
    const taxableBase = currency(taxableItemsSubTotal).add(positiveAdjustment).value;
    // Check if all items are taxable to determine if full subtotal should be taxed
    const allItemsTaxable = items.length > 0 && items.every((item) => item?.metadata?.isTaxable);
    // If all items are taxable and there's an adjustment, tax the full subtotal
    // Otherwise, tax the taxable items + positive adjustment
    taxes = calculateTaxesFromItems({
        items,
        taxRate,
        taxableBase: allItemsTaxable && adjustment ? subTotal : taxableBase || undefined,
    });
    const result = calculateProcessingFees(currency(subTotal).add(taxes).value, pricingConfig);
    processingFees = clientPaysFees ? result.customerFeesTotal : 0;
    total = currency(subTotal).add(taxes).add(processingFees).value;
    return {
        subTotal: toCurrencyValue(subTotal),
        total: toCurrencyValue(total),
        taxes: toCurrencyValue(taxes),
        processingFees: toCurrencyValue(processingFees),
        merchantProcessingFees: toCurrencyValue(result.totalProcessingFee),
        discount: 0,
        cost: toCurrencyValue(cost),
    };
}
/**
 * Calculates the totals for a payment.
 *
 * @param {object} params - The parameters object.
 * @param {FinancialPaymentTransactionType[]} params.payments - The payments to calculate the totals from.
 * @returns {object} - The totals for the payment.
 *
 * @example
 * const totals = getPaymentTotals(payments);
 */
export function getPaymentTotals(payments) {
    let totalAmount = 0;
    let cash = 0;
    let card = 0;
    let ebt = 0;
    let totalMerchantNetAmount = 0;
    payments.forEach((payment) => {
        if (payment.status !== ActionStatusEnum.SUCCESS)
            return;
        totalAmount = currency(totalAmount).add(payment.amount).value;
        totalMerchantNetAmount = currency(totalMerchantNetAmount).add(payment.merchantNetAmount).value;
        switch (payment.type) {
            case PaymentMethodEnum.CASH:
                cash = currency(cash).add(payment.amount).value;
                break;
            case PaymentMethodEnum.CARD:
            case PaymentMethodEnum.MANUAL_CARD:
            case PaymentMethodEnum.BLE_CONNECTED_READER:
                card = currency(card).add(payment.amount).value;
                break;
            case PaymentMethodEnum.EBT:
                ebt = currency(ebt).add(payment.amount).value;
                break;
            default:
                break;
        }
    });
    return { totalAmount, cash, card, ebt, totalMerchantNetAmount };
}
export function calculateMerchantNetAmount({ payment, totalAmountAfterFees, totalProcessingFee, }) {
    // Skip fee calculation for cash/EBT payments
    if (payment.type === PaymentMethodEnum.CASH ||
        payment.type === PaymentMethodEnum.EBT) {
        return payment.amount;
    }
    // Guard against division by zero — return the full payment amount.
    if (totalAmountAfterFees <= 0) {
        return payment.amount;
    }
    // we apply the paymentRatio to the totalProcessingFee
    const paymentRatio = currency(payment.amount).divide(totalAmountAfterFees).value;
    const merchantFee = currency(totalProcessingFee).multiply(paymentRatio).value;
    return currency(payment.amount).subtract(merchantFee).value;
}
